import { inject, Injectable } from '@angular/core';

import { isFileTypeWildcard, isImage, loadImage } from '../../../../util/public-api';
import { ObjectUrlService } from '../object-url/object-url-service.service';

const MAX_FILE_SIZE_BYTES = 1024 * 1024 * 1024; // 1GB

export type FileValidationError =
  | 'incorrect-type'
  | 'image-too-small'
  | 'file-size-exceeded'
  | 'file-empty'
  | 'file-unreadable'
  | 'unknown-error';

export interface FilePrepareResult<T = File | Blob> {
  file: T;
  objectUrl?: string;
  imageWidth?: number;
  imageHeight?: number;
  errors?: FileValidationError[];
}

@Injectable({ providedIn: 'root' })
export class PrepareFilesService {
  private readonly objectUrlService = inject(ObjectUrlService);

  getErrorMessage(
    fileName: string,
    error: FileValidationError | undefined,
    supportedFileExtensions: string[],
    minimumImageDimensions?: [number, number],
  ): string {
    switch (error) {
      case 'incorrect-type':
        return `${fileName}: Unsupported file type. Supported file types: ${supportedFileExtensions.join(', ')}`;
      case 'image-too-small':
        return `${fileName}: Image too small. Minimal image dimensions are ${minimumImageDimensions?.[0]} x ${minimumImageDimensions?.[1]} px`;
      case 'file-size-exceeded':
        return `${fileName}: File size limit exceeded. Maximum file size is 1GB.`;
      case 'file-empty':
        return `${fileName}: File is empty (0 KB).`;
      case 'file-unreadable':
        return `${fileName}: File could not be read. If it is stored in the cloud (e.g. OneDrive, Google Drive), make sure it is available offline and try again.`;
      case 'unknown-error':
      default:
        return `${fileName}: Unknown error`;
    }
  }

  isSizeValid(fileSize?: number): boolean {
    return !fileSize || fileSize <= MAX_FILE_SIZE_BYTES;
  }

  /**
   * Probes the first byte of the file to verify its content is actually readable.
   * A zero-length read or a read failure means an empty file or a stale handle
   * (e.g. a cloud placeholder not synced locally) that would upload as a 0-byte object.
   */
  async getContentValidationError(file: File | Blob): Promise<FileValidationError | undefined> {
    if (file.size === 0) {
      return 'file-empty';
    }
    try {
      const probe = await readBlobToArrayBuffer(file.slice(0, 1));
      return probe.byteLength > 0 ? undefined : 'file-empty';
    } catch {
      return 'file-unreadable';
    }
  }

  async prepareFile<T extends File | Blob>(
    file: T,
    acceptedFileExtensions: string[],
    minimumImageDimensions?: [number, number],
  ): Promise<FilePrepareResult<T>> {
    const errors: FileValidationError[] = [];
    let objectUrl: string | undefined;
    let imageWidth: number | undefined;
    let imageHeight: number | undefined;

    // File type
    if (
      !isFileTypeWildcard(acceptedFileExtensions) &&
      'name' in file &&
      !acceptedFileExtensions.some((fileExtension) =>
        file['name'].toLocaleLowerCase().endsWith(fileExtension),
      )
    ) {
      errors.push('incorrect-type');
    }

    // File size
    if (!this.isSizeValid(file.size)) {
      errors.push('file-size-exceeded');
    }

    // File content
    const contentError = await this.getContentValidationError(file);
    if (contentError) {
      errors.push(contentError);
    }

    // Image size
    if (isImage(file.type)) {
      objectUrl = window.URL.createObjectURL(file);

      try {
        const image = await loadImage(objectUrl);
        imageWidth = image.naturalWidth;
        imageHeight = image.naturalHeight;

        if (
          minimumImageDimensions &&
          (imageWidth < minimumImageDimensions[0] || imageHeight < minimumImageDimensions[1])
        ) {
          errors.push('image-too-small');
        }
      } catch (e) {
        console.error('[Uploader]', e);
        errors.push('unknown-error');
      }

      // If errors found, revoke URL for better performance
      if (errors && errors.length > 0) {
        window.URL.revokeObjectURL(objectUrl);
        objectUrl = undefined;
      }
    }

    if (objectUrl) {
      // Add object url for global tracking to avoid leaks and get a safe url
      objectUrl = this.objectUrlService.addObjectUrl(objectUrl);
    }

    return {
      file,
      objectUrl,
      imageWidth,
      imageHeight,
      errors,
    };
  }
}

/** `Blob.arrayBuffer()` via `FileReader` — jsdom (unit tests) does not implement the former. */
function readBlobToArrayBuffer(blob: Blob): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (): void => {
      if (reader.result instanceof ArrayBuffer) {
        resolve(reader.result);
      } else {
        reject(new Error('Unexpected FileReader result type'));
      }
    };
    reader.onerror = (): void => {
      reject(reader.error ?? new Error('File read failed'));
    };
    reader.readAsArrayBuffer(blob);
  });
}
